// T-130 (v1.8.38, Session 36, 28.09.2026): Status einer Buchung für den Gast (Kundenkonto, Seite Buchungsdetails).
// Die API liefert status als Objekt { status: 'OfferSent', statusDisplayName: 'Angebot verschickt', ... }; die Seite las bisher
// status.code/status.displayName (gibt es nicht) und zeigte deshalb immer 'Bestätigt' mit Häkchen, auch bei offenen Angeboten
// (Testangebot B260031, Oettershagen 28.09.). Reine Funktion, getestet in buchungsstatus.test.ts.

export type StatusArt =
  | 'bestaetigt'
  | 'angebot'
  | 'angebotUngueltig'
  | 'option'
  | 'anfrage'
  | 'storniert'
  | 'entwurf'
  | 'nichtErschienen'
  | 'unbekannt';

export interface GastStatus {
  code: string;
  text: string;
  art: StatusArt;
  hinweis?: string;
  /** true nur für verbindliche Buchungen: Bestätigungszeile anzeigen */
  verbindlich: boolean;
  /** true, wenn noch eine Zahlung offen sein kann (Zahlungshinweis Anzahlung/Vollzahlung); nicht bei bezahlten oder vergangenen Fahrten */
  zahlungOffen: boolean;
}

const ZAHLUNG_OFFEN = ['OfferAccepted', 'Confirmed', 'PaymentPending', 'AwaitingFinalPayment', 'PartiallyPaid'];

const STATUS: Record<string, [string, StatusArt]> = {
  Draft: ['Entwurf', 'entwurf'],
  Option: ['Option (vorläufig reserviert)', 'option'],
  OfferSent: ['Angebot offen', 'angebot'],
  OfferAccepted: ['Angebot angenommen', 'bestaetigt'],
  OfferDeclined: ['Angebot abgelehnt', 'angebotUngueltig'],
  OfferExpired: ['Angebot abgelaufen', 'angebotUngueltig'],
  PendingApproval: ['Anfrage in Prüfung', 'anfrage'],
  Confirmed: ['Bestätigt', 'bestaetigt'],
  PaymentPending: ['Bestätigt, Zahlung ausstehend', 'bestaetigt'],
  AwaitingFinalPayment: ['Bestätigt, Restzahlung offen', 'bestaetigt'],
  PartiallyPaid: ['Bestätigt, teilweise bezahlt', 'bestaetigt'],
  FullyPaid: ['Bestätigt und bezahlt', 'bestaetigt'],
  Paid: ['Bestätigt und bezahlt', 'bestaetigt'],
  InProgress: ['Fahrt läuft', 'bestaetigt'],
  Completed: ['Abgeschlossen', 'bestaetigt'],
  Cancelled: ['Storniert', 'storniert'],
  NoShow: ['Nicht erschienen', 'nichtErschienen'],
};

const HINWEIS: Partial<Record<StatusArt, string>> = {
  angebot: 'Dieses Angebot ist noch nicht angenommen. Sie können es hier über „Angebot ansehen und annehmen“ annehmen oder über die Schaltfläche „Angebot annehmen“ in unserer Angebots-Mail.',
  angebotUngueltig: 'Dieses Angebot ist nicht mehr gültig. Bei Interesse melden Sie sich bitte bei uns.',
  option: 'Die Plätze sind für Sie vorläufig reserviert. Verbindlich wird die Buchung mit unserer Bestätigung.',
  anfrage: 'Ihre Buchungsanfrage wird von uns geprüft. Sie erhalten eine Nachricht per E-Mail.',
  storniert: 'Diese Buchung ist storniert.',
  entwurf: 'Diese Buchung ist noch in Bearbeitung.',
};

/** Liest den Statuscode aus den möglichen API-Formen (Objekt mit status/code oder Text). */
export function statusCode(status: unknown): string {
  if (typeof status === 'string') return status;
  if (status && typeof status === 'object') {
    const s = status as { status?: unknown; code?: unknown };
    if (typeof s.status === 'string') return s.status;
    if (typeof s.code === 'string') return s.code;
  }
  return '';
}

export function gastStatus(status: unknown): GastStatus {
  const code = statusCode(status);
  const bekannt = STATUS[code];
  if (bekannt) {
    const [text, art] = bekannt;
    return { code, text, art, hinweis: HINWEIS[art], verbindlich: art === 'bestaetigt', zahlungOffen: ZAHLUNG_OFFEN.includes(code) };
  }
  // Unbekannt: Anzeigetext der API, sonst neutral; nie ein erfundenes 'Bestätigt'
  let text = 'Status wird ermittelt';
  if (status && typeof status === 'object') {
    const s = status as { statusDisplayName?: unknown; displayName?: unknown };
    if (typeof s.statusDisplayName === 'string' && s.statusDisplayName) text = s.statusDisplayName;
    else if (typeof s.displayName === 'string' && s.displayName) text = s.displayName;
  } else if (code) {
    text = code;
  }
  return { code, text, art: 'unbekannt', verbindlich: false, zahlungOffen: false };
}
